import { useState } from "react";
import { motion } from "framer-motion";
import { Flame, Loader2, TrendingUp, TrendingDown, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export const TopPicksBoard = ({ sport, source = "demo" }) => {
  const [loading, setLoading] = useState(false);
  const [picks, setPicks] = useState(null);
  const [note, setNote] = useState(null);

  const run = async (refresh = false) => {
    setLoading(true);
    setNote(null);
    if (!refresh) setPicks(null);
    try {
      const res = await api.get("/ai/top-picks", {
        params: { sport, source, ...(refresh ? { refresh: true } : {}) },
      });
      setPicks(res.data.picks || []);
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not load best options.";
      if (e.response?.status === 404) {
        setNote(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#233248] bg-[#141C2B] p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-[#F59E0B]/15 grid place-items-center">
            <Flame className="h-4 w-4 text-[#F59E0B]" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-white text-base leading-tight">Best Options — AI Ranked</h3>
            <p className="text-[11px] text-slate-400">
              {sport} highest-value plays across the {source === "live" ? "loaded" : ""} slate
            </p>
          </div>
        </div>
        <button
          data-testid="load-top-picks-button"
          onClick={() => run(!!picks)}
          disabled={loading}
          className="flex items-center gap-2 rounded-lg bg-[#00E5FF] text-[#0B0F17] text-sm font-semibold px-3 py-2 hover:bg-[#33EaFF] disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trophy className="h-4 w-4" />}
          {loading ? "Ranking…" : picks ? "Refresh" : "Rank Best Options"}
        </button>
      </div>

      {note && !loading && (
        <p className="text-sm text-slate-400 text-center py-6" data-testid="top-picks-note">
          {note}
        </p>
      )}

      {!picks && !loading && !note && (
        <p className="text-sm text-slate-500 text-center py-6">
          Tap <span className="text-[#00E5FF]">Rank Best Options</span> to let the AI grade the {sport} slate and surface the sharpest plays.
        </p>
      )}

      {picks && (
        <div className="grid sm:grid-cols-2 gap-2.5" data-testid="top-picks-list">
          {picks.map((p, i) => {
            const over = (p.lean || "").toUpperCase() === "OVER";
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 rounded-xl border border-[#233248] bg-[#0B0F17]/50 p-3"
              >
                <span className="font-heading font-extrabold text-xl text-slate-600 w-6 text-center">{p.rank}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-white font-semibold text-sm truncate">{p.player_name}</div>
                  <div className="font-data text-[11px] text-slate-500">
                    {p.team} vs {p.opponent}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {over ? (
                      <TrendingUp className="h-3 w-3 text-[#10B981]" />
                    ) : (
                      <TrendingDown className="h-3 w-3 text-[#EF4444]" />
                    )}
                    <span className="font-data text-xs text-slate-300">
                      {p.lean} {p.line} {p.metric}
                    </span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-data font-bold text-[#00E5FF]">{p.confidence}%</div>
                  <div className="font-data text-[11px] text-[#10B981]">{p.ev}</div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};
